/**
 * Shared utility for address generation from mnemonic phrases
 * 
 * This utility provides a single source of truth for address generation logic,
 * eliminating duplication across useOnboard, useAgentDelegation, and useAddressGenerator hooks.
 */

import mormSigner from "../sdk/signing/mormSigner";
import type { ChainType, AddressResult, MultichainAddressResult } from "../sdk/types";

/**
 * Generate an address from a mnemonic phrase for a specific chain type
 * 
 * @param mnemonic - The mnemonic phrase (will be trimmed)
 * @param chainType - The chain type to generate address for
 * @param addressIndex - The address index for derivation (default: 0)
 * @param isMainnet - Whether to use mainnet (default: true)
 * @returns Promise resolving to AddressResult or MultichainAddressResult
 */
export async function generateAddressFromMnemonic(
  mnemonic: string,
  chainType: ChainType,
  addressIndex: number = 0,
  isMainnet: boolean = true,
): Promise<AddressResult | MultichainAddressResult> {
  const trimmedMnemonic = mnemonic.trim();

  if (!trimmedMnemonic) {
    return {
      success: false,
      error: "Mnemonic phrase is required",
    };
  }

  // Ensure WASM module is ready
  if (!mormSigner.isReady()) {
    try {
      await mormSigner.initialize();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to initialize WASM module",
      };
    }
  }

  try {
    // Use multichain generation for non-Ethereum chains
    if (chainType !== "ethereum") {
      const result = await mormSigner.generateAddressChain(
        trimmedMnemonic,
        chainType,
        addressIndex,
        isMainnet,
      );
      return result;
    } else {
      // Use Ethereum generation (backward compatible)
      const result = await mormSigner.generateAddress(
        trimmedMnemonic,
        addressIndex,
      );
      return result;
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error during address generation",
    };
  }
}

/**
 * Generate an Ethereum address from a mnemonic phrase (backward compatibility helper)
 * 
 * @param mnemonic - The mnemonic phrase (will be trimmed)
 * @param addressIndex - The address index for derivation (default: 0)
 * @returns Promise resolving to AddressResult
 */
export async function generateEthereumAddress(
  mnemonic: string,
  addressIndex: number = 0,
): Promise<AddressResult> {
  return generateAddressFromMnemonic(mnemonic, "ethereum", addressIndex, true) as Promise<AddressResult>;
}
